import type { LucideIcon } from "lucide-react";
import {
  BadgeDollarSign,
  CheckCircle2,
  FileText,
  Send,
  UserCheck,
} from "lucide-react";
import type {
  SolicitacaoPagamentoDto,
  SolicitacaoPagamentoStatus,
} from "@/services/solicitacaoPagamento";
import type { SolicitacaoListMode } from "./SolicitacaoPagamentoDetailModal";

export type ApprovalStageId =
  | "solicitacao"
  | "operacional"
  | "financeira"
  | "sienge"
  | "concluida";

export interface ApprovalStageDef {
  id: ApprovalStageId;
  step: number;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  pillBg: string;
  pillText: string;
  textColor: string;
}

export const APPROVAL_STAGES: ApprovalStageDef[] = [
  {
    id: "solicitacao",
    step: 1,
    title: "Solicitação de Pagamento",
    subtitle: "Solicitações criadas",
    icon: FileText,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    pillBg: "bg-blue-50",
    pillText: "text-blue-700",
    textColor: "text-blue-700",
  },
  {
    id: "operacional",
    step: 2,
    title: "Aprovação Operacional",
    subtitle: "Aguardando aprovação operacional",
    icon: UserCheck,
    iconBg: "bg-orange-50",
    iconColor: "text-orange-600",
    pillBg: "bg-orange-50",
    pillText: "text-orange-700",
    textColor: "text-orange-700",
  },
  {
    id: "financeira",
    step: 3,
    title: "Aprovação Financeira",
    subtitle: "Em aprovação financeira",
    icon: BadgeDollarSign,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    pillBg: "bg-emerald-50",
    pillText: "text-emerald-700",
    textColor: "text-emerald-700",
  },
  {
    id: "sienge",
    step: 4,
    title: "Aguardando Envio ao Sienge",
    subtitle: "Aguardando envio ao Sienge",
    icon: Send,
    iconBg: "bg-violet-50",
    iconColor: "text-violet-600",
    pillBg: "bg-violet-50",
    pillText: "text-violet-700",
    textColor: "text-violet-700",
  },
  {
    id: "concluida",
    step: 5,
    title: "Títulos Gerados",
    subtitle: "Títulos inseridos no Sienge",
    icon: CheckCircle2,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    pillBg: "bg-green-50",
    pillText: "text-green-700",
    textColor: "text-green-700",
  },
];

const STAGE_BY_STATUS: Record<SolicitacaoPagamentoStatus, ApprovalStageId> = {
  reprovada: "solicitacao",
  pendente_aprovacao: "operacional",
  pendente_financeira: "financeira",
  aguardando_sienge: "sienge",
  aprovada: "concluida",
  cancelada: "solicitacao",
};

export function getStageIdForItem(item: SolicitacaoPagamentoDto): ApprovalStageId {
  return STAGE_BY_STATUS[item.status];
}

export function getStageDefForItem(item: SolicitacaoPagamentoDto): ApprovalStageDef {
  const id = getStageIdForItem(item);
  return APPROVAL_STAGES.find((s) => s.id === id) ?? APPROVAL_STAGES[0];
}

export function getListModeForItem(item: SolicitacaoPagamentoDto): SolicitacaoListMode {
  switch (item.status) {
    case "reprovada":
      return "minhas";
    case "pendente_aprovacao":
      return "operacional";
    case "pendente_financeira":
      return "financeira";
    case "aguardando_sienge":
    case "aprovada":
      return "sienge";
    default:
      return "minhas";
  }
}

export function getStageSubtext(item: SolicitacaoPagamentoDto): string {
  switch (item.status) {
    case "reprovada":
      return "Aguardando correção";
    case "pendente_aprovacao":
      return "Aguardando análise";
    case "pendente_financeira":
      return "Em análise financeira";
    case "aguardando_sienge":
      return "Pronta para envio";
    case "aprovada":
      return "Título gerado no Sienge";
    default:
      return "";
  }
}

export type DisplayStatusKind = "andamento" | "pronta" | "concluida" | "reprovada";

export function getDisplayStatus(item: SolicitacaoPagamentoDto): {
  kind: DisplayStatusKind;
  label: string;
} {
  switch (item.status) {
    case "aguardando_sienge":
      return { kind: "pronta", label: "Pronta" };
    case "aprovada":
      return { kind: "concluida", label: "Concluída" };
    case "reprovada":
      return { kind: "reprovada", label: "Reprovada" };
    default:
      return { kind: "andamento", label: "Em andamento" };
  }
}

export function countByStage(items: SolicitacaoPagamentoDto[]): Record<ApprovalStageId, number> {
  const counts: Record<ApprovalStageId, number> = {
    solicitacao: 0,
    operacional: 0,
    financeira: 0,
    sienge: 0,
    concluida: 0,
  };
  for (const item of items) {
    counts[getStageIdForItem(item)] += 1;
  }
  return counts;
}

const AVATAR_COLORS = [
  "bg-pink-100 text-pink-700",
  "bg-purple-100 text-purple-700",
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-orange-100 text-orange-700",
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-cyan-100 text-cyan-700",
];

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function mergeUniqueSolicitacoes(
  ...lists: SolicitacaoPagamentoDto[][]
): SolicitacaoPagamentoDto[] {
  const map = new Map<string, SolicitacaoPagamentoDto>();
  for (const list of lists) {
    for (const item of list) {
      map.set(item.id, item);
    }
  }
  return [...map.values()];
}

/** Solicitações criadas pelo usuário ou que exigem ação/aprovação dele. */
export function filterMinhasSolicitacoes(
  items: SolicitacaoPagamentoDto[],
  userId: string,
  opts: {
    manageAll: boolean;
    canAprovarFinanceira: boolean;
  },
): SolicitacaoPagamentoDto[] {
  if (!userId) return [];

  // Usuário comum: o backend já devolve apenas criadas + filas de aprovação do usuário.
  if (!opts.manageAll) return items;

  return items.filter((item) => {
    if (item.solicitanteId === userId) return true;
    if (item.aprovadorId === userId) return true;
    if (item.financeiroReviewedById === userId) return true;
    if (
      opts.canAprovarFinanceira &&
      (item.status === "pendente_financeira" || item.status === "aguardando_sienge")
    ) {
      return true;
    }
    return false;
  });
}
